// Body of the Oscar account order-history list: the legacy sealy orders page
// -- the "Your Account / Your Orders" breadcrumbs, the (possibly CSR
// owner-named) heading, one shared RichOrderDetail block per order with the
// shared pagination, the two legacy empty states, and the customer-service
// info block. The rich blocks are typed-serialized server-side (rich_orders);
// each order-details toggle is owned by its shared block's own disclosure
// state (collapsible).
import { Immutable } from "immer";
import React from "react";
import { Pagination } from "tsi-common-react/src/apps/customer-account/Pagination/index";
import { RichOrderDetail } from "tsi-common-react/src/apps/customer-account/RichOrderDetail/index";

import { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { AccountBreadcrumbs } from "../AccountBreadcrumbs";
import { CustomerServiceInfoBlock } from "../CustomerServiceInfoBlock";

import orderListStyles from "../../styles/composable/order-list.module.scss";

type IProps = Immutable<templates.OrderHistoryDocumentTemplate>;

export const OrderHistoryPageBody = (props: IProps): React.JSX.Element => {
    const richOrders = props.rich_orders ?? [];
    return (
        <>
            <div
                className={concatClassNames([
                    "l-capped-width",
                    "u-margin-bottom--xl",
                    "u-full-width-title",
                    orderListStyles.orderList,
                ])}
                data-testid="order-list"
            >
                <AccountBreadcrumbs
                    crumbs={[
                        { label: "Your Account", href: props.profile_url },
                        { label: "Your Orders" },
                    ]}
                />

                <h2>{props.orders_heading}</h2>

                <div
                    className={concatClassNames([
                        "content__copy--full-width",
                        orderListStyles.ordersContent,
                        richOrders.length
                            ? null
                            : orderListStyles.ordersContentEmpty,
                    ])}
                    data-testid="order-list-content"
                >
                    <div className={orderListStyles.orders}>
                        <ul className={orderListStyles.ordersList}>
                            {richOrders.length ? (
                                <>
                                    {richOrders.map((order, i) => (
                                        <li
                                            key={i}
                                            className={
                                                orderListStyles.ordersListItem
                                            }
                                            data-testid="order-list-item"
                                        >
                                            <div
                                                className={concatClassNames([
                                                    "content__copy--full-width",
                                                    orderListStyles.orderSummaryWrapper,
                                                ])}
                                            >
                                                <RichOrderDetail
                                                    {...order}
                                                    collapsible={true}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                    {props.pagination && (
                                        <Pagination data={props.pagination} />
                                    )}
                                </>
                            ) : props.form_is_bound ? (
                                <li
                                    className={orderListStyles.ordersListItem}
                                    data-testid="order-list-item"
                                >
                                    No orders match your search.
                                </li>
                            ) : (
                                <li
                                    className={concatClassNames([
                                        orderListStyles.ordersListItem,
                                        orderListStyles.emptyListItem,
                                    ])}
                                    data-testid="order-list-item"
                                >
                                    <h2
                                        className={
                                            orderListStyles.emptyListItemTitle
                                        }
                                    >
                                        You haven&apos;t shopped with us.
                                    </h2>
                                    <p
                                        className={
                                            orderListStyles.emptyListItemCopy
                                        }
                                    >
                                        Please check back after your first
                                        purchase to view your order details.
                                    </p>
                                    <a
                                        className="button"
                                        href={props.root_page_url}
                                    >
                                        Shop now
                                    </a>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>

            {props.cs_info && <CustomerServiceInfoBlock info={props.cs_info} />}
        </>
    );
};
